// src/utils/firestoreBatch.ts
//
// Dávkový zápis do Firestore s limitem operací na dávku (krok 9 redesignu).
// Dřív dvě kopie téže smyčky: publishResults.ts (limit 450) a
// AdminResultsUpload.tsx (limit 400 – mazání starých výsledků i zápis nových).
//
// Firestore pustí do jedné dávky (writeBatch) nejvýš 500 operací. Pomocník
// sbírá operace do aktuální dávky, po dosažení limitu ji sám odešle a pokračuje
// novou; commit() odešle zbytek. Dávky se odesílají postupně v pořadí vložení
// (další až po úspěchu předchozí), takže pořadí zápisů platí i přes hranici
// dávek – např. bump races.resultsPublishedAt zařazený jako poslední se zapíše
// až po všech výsledcích.
//
// Atomická je vždy jen jedna dávka, ne celý zápis: když odeslání selže,
// dřívější dávky zůstanou zapsané. Volající proto řadí zápisy tak, aby
// opakování po chybě dopadlo stejně (idempotence) a „potvrzující" zápis
// (metadata, bump) šel poslední.
//
// Chyby se propagují: odmítne se promise operace, která dávku odeslala,
// i commit(). Po chybě odeslání pomocník další operace nepřijme (odmítne je
// stejnou chybou) a nic dalšího neodešle – polovina zápisu bez povšimnutí
// nevznikne. Chyba validace dat (např. undefined v dokumentu – Firestore ji
// hlásí hned při vložení) odmítne jen tu jednu operaci; dávka zůstává
// použitelná.
//
// Použití – každou operaci awaitovat (jinak se chyba odeslání ztratí):
//   const writer = createBatchWriter(db);
//   for (const d of snap.docs) await writer.delete(d.ref);
//   await writer.set(doc(db, "races", id), data);
//   await writer.commit();

import {
  writeBatch,
  type DocumentData,
  type DocumentReference,
  type Firestore,
  type PartialWithFieldValue,
  type SetOptions,
  type UpdateData,
  type WithFieldValue,
  type WriteBatch,
} from "firebase/firestore";

/** Výchozí počet operací v dávce – rezerva pod limitem Firestore (500). */
export const DEFAULT_BATCH_LIMIT = 400;

/** Limit Firestore pro jednu dávku (writeBatch). */
export const MAX_BATCH_LIMIT = 500;

export interface BatchWriterOptions {
  /** Max. operací v jedné dávce (celé číslo 1–500); výchozí DEFAULT_BATCH_LIMIT. */
  limit?: number;
}

export interface BatchWriter {
  /** batch.set – po dosažení limitu dávku odešle (promise čeká na odeslání). */
  set<T, D extends DocumentData>(
    ref: DocumentReference<T, D>,
    data: WithFieldValue<T>
  ): Promise<void>;
  set<T, D extends DocumentData>(
    ref: DocumentReference<T, D>,
    data: PartialWithFieldValue<T>,
    options: SetOptions
  ): Promise<void>;
  /** batch.update (dokument musí existovat, jinak selže odeslání celé dávky). */
  update<T, D extends DocumentData>(
    ref: DocumentReference<T, D>,
    data: UpdateData<D>
  ): Promise<void>;
  /** batch.delete (smazání neexistujícího dokumentu není chyba). */
  delete<T, D extends DocumentData>(ref: DocumentReference<T, D>): Promise<void>;
  /** Odešle zbývající operace a počká na všechna odeslání; bez operací nic neodesílá. */
  commit(): Promise<void>;
  /** Operace v aktuální, ještě neodeslané dávce. */
  readonly pending: number;
  /** Operace v úspěšně odeslaných dávkách. */
  readonly committed: number;
}

/** Pomocník pro dávkový zápis s automatickým odesláním po `limit` operacích (viz hlavička). */
export function createBatchWriter(
  db: Firestore,
  options: BatchWriterOptions = {}
): BatchWriter {
  const limit = options.limit === undefined ? DEFAULT_BATCH_LIMIT : options.limit;
  if (!Number.isInteger(limit) || limit < 1 || limit > MAX_BATCH_LIMIT) {
    throw new RangeError(
      `Limit dávky musí být celé číslo 1–${MAX_BATCH_LIMIT} (zadáno ${String(limit)}).`
    );
  }

  let batch: WriteBatch = writeBatch(db);
  let pending = 0;
  let committed = 0;
  /** Řetěz odesílání – další dávka až po úspěchu předchozí. */
  let chain: Promise<void> = Promise.resolve();
  /** První chyba odeslání; po ní pomocník nic dalšího nepřijme ani neodešle. */
  let failure: { error: unknown } | null = null;

  /**
   * Zařadí aktuální dávku k odeslání a hned založí novou (synchronně), takže
   * operace vložené během odesílání jdou do další dávky, ne do odeslané.
   */
  const send = (): Promise<void> => {
    if (pending > 0) {
      const full = batch;
      const count = pending;
      batch = writeBatch(db);
      pending = 0;
      chain = chain.then(() =>
        full.commit().then(
          () => {
            committed += count;
          },
          (error: unknown) => {
            if (!failure) failure = { error };
            throw error;
          }
        )
      );
    }
    return chain;
  };

  const queue = (apply: (current: WriteBatch) => void): Promise<void> => {
    if (failure) return Promise.reject(failure.error);
    try {
      apply(batch);
    } catch (error) {
      // Neplatná data – Firestore operaci do dávky nepřidal, dávka zůstává použitelná
      return Promise.reject(error);
    }
    pending += 1;
    return pending >= limit ? send() : Promise.resolve();
  };

  function set<T, D extends DocumentData>(
    ref: DocumentReference<T, D>,
    data: WithFieldValue<T>
  ): Promise<void>;
  function set<T, D extends DocumentData>(
    ref: DocumentReference<T, D>,
    data: PartialWithFieldValue<T>,
    setOptions: SetOptions
  ): Promise<void>;
  function set<T, D extends DocumentData>(
    ref: DocumentReference<T, D>,
    data: PartialWithFieldValue<T>,
    setOptions?: SetOptions
  ): Promise<void> {
    return queue((current) => {
      if (setOptions) current.set(ref, data, setOptions);
      else current.set(ref, data as WithFieldValue<T>);
    });
  }

  return {
    set,
    update: (ref, data) =>
      queue((current) => {
        current.update(ref, data);
      }),
    delete: (ref) =>
      queue((current) => {
        current.delete(ref);
      }),
    commit: () => (failure ? Promise.reject(failure.error) : send()),
    get pending() {
      return pending;
    },
    get committed() {
      return committed;
    },
  };
}
